import type {
  StaffAttentionApi,
  StaffHomeTrendsApi,
  StaffRequestCreateApi,
  StaffTeamLoadApi,
} from "@lafayette/api-client";
import { createContext, type ReactNode, useContext } from "react";

// What Home and the shell need from outside: the typed attention client and, for a test, the clock. The admin app supplies the real one (`admin-runtime.ts`); a test or
// the development harness supplies a fake.

export type HomeRuntime = Readonly<{
  api: StaffAttentionApi;
  /** The display name of the signed-in staff member (`GET /staff/me`), for the greeting. Left out: the greeting has no name. */
  displayName?: (options?: { signal?: AbortSignal }) => Promise<string>;
  /** The numbers behind the stat tiles (`GET /staff/home-trends`). Left out: Home draws no stat tiles. */
  trends?: StaffHomeTrendsApi;
  /** The team load card (`GET /staff/team/load`, supervisors). Left out: Home draws no team load card. */
  teamLoad?: StaffTeamLoadApi;
  /** "New request" (`POST /staff/customers/:customerId/concierge-requests`, CHT-2051). Left out: Home draws no "New request" button. */
  requestCreate?: StaffRequestCreateApi;
  /** The clock of the greeting ("Good morning", the date). Left out: the clock of the machine. A test fixes it. */
  now?: () => Date;
}>;

const RuntimeContext = createContext<HomeRuntime | null>(null);

export function HomeRuntimeProvider({
  value,
  children,
}: Readonly<{ value: HomeRuntime; children: ReactNode }>) {
  return <RuntimeContext.Provider value={value}>{children}</RuntimeContext.Provider>;
}

export function useHomeRuntime(): HomeRuntime {
  const runtime = useContext(RuntimeContext);
  if (runtime === null) throw new Error("Home needs a HomeRuntimeProvider.");
  return runtime;
}

/** The runtime when a provider is mounted, else `null`: for a link that may sit on a page mounted without Home (a unit test). */
export function useOptionalHomeRuntime(): HomeRuntime | null {
  return useContext(RuntimeContext);
}
